// Special dates with guest menus (20260925090010) — the pure rules the panel,
// the CTA and the cart share. A date with two or more enabled guest menus
// asks how many guests take each; the split must equal the party and the
// hold is priced per menu on the server. Everything here is display and
// gating: the server (create_booking_hold → _special_date_menu_price) is the
// authority and refuses a split that does not add up.

import type { WidgetSpecialDateEvent, WidgetSpecialDateGuestMenu } from "../types";

/** menu id → guests taking that menu. */
export type GuestMenuSplit = Record<string, number>;

/** The menus a guest may choose from, in display order. Never undefined. */
export function guestMenusOf(event: Pick<WidgetSpecialDateEvent, "guest_menus">): WidgetSpecialDateGuestMenu[] {
  return (event.guest_menus ?? [])
    .filter((m) => m.is_enabled !== false)
    .slice()
    .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name));
}

/** Two or more menus: every guest picks one. Fewer: the date's own price applies. */
export function offersMenuChoice(event: Pick<WidgetSpecialDateEvent, "guest_menus">): boolean {
  return guestMenusOf(event).length >= 2;
}

export function splitTotal(split: GuestMenuSplit): number {
  return Object.values(split).reduce((n, q) => n + (q > 0 ? q : 0), 0);
}

/** Only the menus the date still offers, with a positive count. */
export function menuLinesFromSplit(
  event: Pick<WidgetSpecialDateEvent, "guest_menus">,
  split: GuestMenuSplit,
): { menu_id: string; qty: number }[] {
  const offered = new Set(guestMenusOf(event).map((m) => m.id));
  return Object.entries(split)
    .filter(([id, qty]) => offered.has(id) && qty > 0)
    .map(([menu_id, qty]) => ({ menu_id, qty }));
}

/**
 * What the guest pays: Σ qty × each menu's price when the date offers a
 * choice, else the date's price × party. The server prices the hold; this
 * is what the CTA and the cart show before the hold answers.
 */
export function specialDateTotalCents(
  event: Pick<WidgetSpecialDateEvent, "guest_menus" | "price_per_guest_cents">,
  partySize: number,
  split: GuestMenuSplit,
): number {
  const menus = guestMenusOf(event);
  if (menus.length < 2) return event.price_per_guest_cents * partySize;
  return menus.reduce((sum, m) => sum + (split[m.id] ?? 0) * m.price_per_guest_cents, 0);
}

/**
 * Why Add is not available yet, or null. The party is the number the guest
 * chose (fixed chips) or the sum of the split (variable range).
 */
export function specialDateAddBlocker(
  event: Pick<WidgetSpecialDateEvent, "guest_menus" | "guest_selection_mode" | "party_size_min" | "party_size_max">,
  partySize: number,
  split: GuestMenuSplit,
): string | null {
  if (!offersMenuChoice(event)) return null;
  const total = splitTotal(split);
  if (event.guest_selection_mode === "variable") {
    const lo = Math.max(1, event.party_size_min ?? 1);
    const hi = event.party_size_max ?? null;
    if (total < lo) return `Add at least ${lo} guest${lo === 1 ? "" : "s"} across the menus`;
    if (hi != null && total > hi) return `At most ${hi} guests on this date`;
    return null;
  }
  if (total < partySize) {
    const short = partySize - total;
    return `Choose a menu for ${short} more guest${short === 1 ? "" : "s"}`;
  }
  if (total > partySize) {
    const over = total - partySize;
    return `${over} guest${over === 1 ? "" : "s"} too many — pick a bigger party or fewer menus`;
  }
  return null;
}

/**
 * The party the date will take, as close as possible to the one the guest
 * brought (the panel seeds 2; the guest may have stepped to 3 on an ordinary
 * day). Fixed chips: the smallest chip at or above it, else the largest.
 * Variable range: clamped into min..max. No chips: unchanged. Without this
 * the CTA quotes a party the server refuses (party_not_allowed).
 */
export function snapPartyToSpecialDate(
  event: Pick<
    WidgetSpecialDateEvent,
    "guest_selection_mode" | "party_size_options" | "party_size_min" | "party_size_max"
  >,
  partySize: number,
): number {
  if (event.guest_selection_mode === "variable") {
    const lo = Math.max(1, event.party_size_min ?? 1);
    const hi = event.party_size_max ?? null;
    if (partySize < lo) return lo;
    if (hi != null && partySize > hi) return Math.max(lo, hi);
    return partySize;
  }
  const chips = (event.party_size_options ?? []).filter((n) => n > 0).slice().sort((a, b) => a - b);
  if (chips.length === 0 || chips.includes(partySize)) return partySize;
  return chips.find((n) => n >= partySize) ?? chips[chips.length - 1]!;
}

/**
 * The notice shown while the date has moved the guest's party: the guest
 * brought `own`, the date took `snapped`. Null when nothing moved. The panel
 * puts the guest's own party back when they leave for an ordinary date.
 */
export function describePartySnap(
  event: Pick<
    WidgetSpecialDateEvent,
    "guest_selection_mode" | "party_size_options" | "party_size_min" | "party_size_max"
  >,
  own: number,
  snapped: number,
): string | null {
  if (own === snapped) return null;
  let takes: string;
  if (event.guest_selection_mode === "variable") {
    const lo = Math.max(1, event.party_size_min ?? 1);
    const hi = event.party_size_max ?? null;
    takes = hi != null ? `${lo}–${Math.max(lo, hi)} guests` : `${lo} or more guests`;
  } else {
    const chips = (event.party_size_options ?? []).filter((n) => n > 0).slice().sort((a, b) => a - b);
    takes =
      chips.length > 1
        ? `parties of ${chips.slice(0, -1).join(", ")} or ${chips[chips.length - 1]}`
        : `parties of ${chips[0] ?? snapped}`;
  }
  return `This date takes ${takes}, so your party of ${own} is set to ${snapped} — change it above if you need to.`;
}

/**
 * menu_selection_required with no menu chooser on screen: the page loaded
 * before the date gained its second menu, so the only way forward is a
 * reload. With the chooser showing, the shared (picker-neutral) copy stands.
 */
export function staleMenuRefusal(reason: string, menuChoiceOnScreen: boolean): string | null {
  return reason === "menu_selection_required" && !menuChoiceOnScreen
    ? "This date now asks each guest to choose a menu. Reload to see the menus, then try again."
    : null;
}

/** Step one menu's count, never below zero; in fixed mode never past the party. */
export function stepSplit(
  split: GuestMenuSplit,
  menuId: string,
  delta: number,
  cap: number | null,
): GuestMenuSplit {
  const current = split[menuId] ?? 0;
  const others = splitTotal(split) - current;
  let next = Math.max(0, current + delta);
  if (cap != null) next = Math.min(next, Math.max(0, cap - others));
  const out = { ...split };
  if (next === 0) delete out[menuId];
  else out[menuId] = next;
  return out;
}
